<script setup lang="ts">
import type { VirtualElement } from "@floating-ui/dom";
import type { SuggestionProps } from "@tiptap/suggestion";
import MenuContent from "ls/components/primitives/menu/MenuContent.vue";
import { PopoverAnchor, PopoverContent, PopoverPortal, PopoverRoot } from "reka-ui";
import { ref, watch, watchEffect } from "vue";
import { defaultRect } from "../../composables/useFloating";
import { getEditorElement } from "../useEditorRootElement";
import { useSuggestionContext } from "./context";

const props = defineProps<SuggestionProps>();

const lastValidClientRect = ref<DOMRect>();
watchEffect(() => {
  // store valid client rect, and use it when suggestion list is about to be detached from the DOM
  // otherwise list will jump to (0,0)
  const clientRect = props.clientRect?.();
  if (clientRect) {
    lastValidClientRect.value = clientRect;
  }
});

const anchor = ref<VirtualElement>({
  contextElement: getEditorElement(props.editor),
  getBoundingClientRect() {
    const clientRect = props.clientRect?.();
    return clientRect ?? lastValidClientRect.value ?? defaultRect();
  },
});

const {
  handleAfterLeave,
  isOpen,
  reset,
  isLoading,
} = useSuggestionContext();

// reset current selected when query changes
watch(() => props.query, reset);
</script>

<template>
  <!-- Wrap in div to workaround tiptap vue rendering single child in teleport -->
  <div data-axure-suggestion-root>
    <PopoverRoot default-open>
      <PopoverAnchor :reference="anchor" />
      <PopoverPortal>
        <PopoverContent
          :collision-padding="10"
          update-position-strategy="always"
          side="bottom"
          align="start"
          force-mount
        >
          <Transition
            appear
            enter-active-class="origin-top-left transition"
            enter-from-class="translate-y-2 scale-95 opacity-0"
            enter-to-class="scale-100 opacity-100"
            leave-active-class="transition"
            leave-from-class="opacity-100"
            leave-to-class="translate-y-2 opacity-0"
            @after-leave="handleAfterLeave"
          >
            <MenuContent
              v-if="isOpen || isLoading"
              role="menu"
              class="max-h-(--reka-popover-content-available-height) min-h-0 max-w-(--reka-popover-content-available-width) overflow-auto empty:hidden"
            >
              <slot />
            </MenuContent>
          </Transition>
        </PopoverContent>
      </PopoverPortal>
    </PopoverRoot>
  </div>
</template>
